import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import LocationMap from '../components/LocationMap';
import { tr } from '../til';

const card = {
  background: '#fff',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)'
};

const REASON_LABELS = {
  texnik: tr("Texnik muammo"),
  shaxsiy: tr("Shaxsiy sabab"),
  batareya: tr("Batareya tejash"),
  boshqa: tr("Boshqa")
};

const TYPE_LABELS = {
  kelish: tr("Keldi"),
  chiqish: tr("Hududdan chiqdi"),
  qaytish: tr("Qaytdi"),
  ketish: tr("Ish tugadi")
};

// Tizim qo'ygan yozuv — xodim bosgan tugma bilan aralashmasligi kerak.
const turNomi = (row) => {
  if (!row.avtomatik) return TYPE_LABELS[row.type] || row.type;
  return row.type === 'chiqish' ? tr("Tekshiruvga chiqdi (tizim)") : tr("Qaytdi (tizim)");
};

const formatTime = (raw) => {
  const d = new Date(raw);
  if (isNaN(d)) return '';
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

const formatDateTime = (raw) => {
  const d = new Date(raw);
  if (isNaN(d)) return '';
  return `${d.toLocaleDateString('uz-UZ')} ${formatTime(raw)}`;
};

// Xodimning holatini bitta joyda aniqlaymiz — jadval ham, hisoblagichlar
// ham shu mantiqdan foydalanadi.
const getStatus = (row) => {
  if (!row.created_at) return 'belgilanmagan';
  // Soxta joylashuv qolgan hamma narsadan ustun: koordinata "to'g'ri"
  // ko'rinsa ham unga ishonib bo'lmaydi.
  if (row.is_mocked) return 'soxta_gps';
  // Yozuvni tizim qo'ygan bo'lsa (nazorat tashrifi ish zonasidan
  // tashqarida bajarilgan) — bu ayb emas, ishning o'zi. Shuning uchun
  // alohida holat: rahbar "qayerda yurgani ma'lum" va "nega chiqqani
  // ma'lum emas" ni farqlab turishi kerak.
  if (row.type === 'chiqish' && row.avtomatik) return 'tekshiruvda';
  // Xodim o'zi "hududdan chiqaman" deb izoh yozgan bo'lsa — bu ruxsatli
  // chiqish, "zona tashqarida" deb ayblanmaydi. Zona ichida turib bosgan
  // bo'lsa ham (chiqishdan oldin) shu holat ko'rsatiladi.
  if (row.type === 'chiqish') return 'hududdan_chiqqan';
  if (!row.gps_available) return 'gps_ochiq';
  if (row.zone_id) return 'ish_joyida';
  // Zona umuman biriktirilmagan bo'lsa buni "tashqarida" deb ayblash
  // noto'g'ri — bu adminning e'tiborini talab qiladigan alohida holat.
  if (row.work_zone_id === null || row.work_zone_id === undefined) return 'zonasiz';
  return 'tashqarida';
};

const STATUS_STYLE = {
  soxta_gps: { label: tr("⛔ Soxta GPS"), color: '#fff', bg: '#c93636' },
  ish_joyida: { label: tr("🟢 Ish joyida"), color: '#2d8a2d', bg: '#e9f9ed' },
  tekshiruvda: { label: tr("🔵 Tekshiruvda (maydonda)"), color: '#1a5fb4', bg: '#e7f0fb' },
  hududdan_chiqqan: { label: tr("🟠 Hududdan chiqqan"), color: '#c2621a', bg: '#ffefe0' },
  tashqarida: { label: tr("🟡 Zona tashqarida"), color: '#c47f17', bg: '#fff8e6' },
  gps_ochiq: { label: tr("🔴 GPS o'chiq"), color: '#c93636', bg: '#ffeaea' },
  zonasiz: { label: tr("⚙️ Ish joyi biriktirilmagan"), color: '#7c4ff7', bg: '#f0eaff' },
  belgilanmagan: { label: tr("⚪ Belgilanmagan"), color: '#999', bg: '#f2f2f2' }
};

function Attendance() {
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState('today'); // 'today' | 'history'
  const [date, setDate] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [mapRow, setMapRow] = useState(null);

  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await axios.get(`${API}/attendance/today`, { headers: { authorization: token } });
    setRows(res.data);
    setLoading(false);
  }, [token]);

  const loadHistory = useCallback(async (d) => {
    const res = await axios.get(`${API}/attendance/history${d ? `?date=${d}` : ''}`, {
      headers: { authorization: token }
    });
    setHistory(res.data);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (mode === 'history') loadHistory(date);
  }, [mode, date, loadHistory]);

  const query = search.trim().toLowerCase();
  const visible = rows.filter(r =>
    r.full_name.toLowerCase().includes(query) ||
    (r.badge_number || '').toLowerCase().includes(query)
  );

  const counts = {
    ish_joyida: rows.filter(r => getStatus(r) === 'ish_joyida').length,
    tekshiruvda: rows.filter(r => getStatus(r) === 'tekshiruvda').length,
    hududdan_chiqqan: rows.filter(r => getStatus(r) === 'hududdan_chiqqan').length,
    tashqarida: rows.filter(r => getStatus(r) === 'tashqarida').length,
    gps_ochiq: rows.filter(r => getStatus(r) === 'gps_ochiq').length,
    belgilanmagan: rows.filter(r => getStatus(r) === 'belgilanmagan').length,
    // Ish joyi biriktirilmagan xodimlar — belgilanganidan qat'i nazar
    zonasiz: rows.filter(r => !r.work_zone_id).length,
    soxta_gps: rows.filter(r => r.is_mocked).length
  };

  const tabStyle = (active) => ({
    padding: '8px 18px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    background: active ? '#1a1a2e' : '#fff',
    color: active ? '#fff' : '#666',
    fontSize: '14px',
    cursor: 'pointer'
  });

  return (
    <div>
      <h2 style={{ marginBottom: '8px', fontSize: '22px' }}>{tr("GPS davomat nazorati")}</h2>
      <p style={{ marginTop: 0, marginBottom: '20px', color: '#888', fontSize: '13px' }}>
        {tr("Xodimlar ilovada \"Ish joyidaman\" tugmasini bosganda joylashuvi bir marta olinadi. Doimiy kuzatuv yo'q. Ish vaqtida hududdan chiqsa — sababini izoh qilib yozadi, qaytganida yana \"Ish joyidaman\" tugmasini bosadi.")}
        {' '}
        {tr("Inspektor mahkumga tashrif yoki sayyor sudni qayd etsa, uning joylashuvi shundan ham ma'lum bo'ladi — bunday hollarda tizim davomatga \"Tekshiruvda\" deb o'zi yozib qo'yadi.")}
      </p>

      {counts.soxta_gps > 0 && (
        <div style={{
          background: '#ffeaea', color: '#c93636', padding: '12px 16px',
          borderRadius: '10px', fontSize: '13px', marginBottom: '16px',
          border: '1px solid #f3b6b6'
        }}>
          ⛔ <strong>{tr("{soxta_gps} ta xodimda soxta joylashuv aniqlandi.", { soxta_gps: counts.soxta_gps })}</strong>{tr("Telefonda \"fake GPS\" ilovasi ishlagan — koordinata yozib olindi, lekin \"ish joyida\" deb tan olinmadi. Ro'yxatdan ko'rib chiqing.")}
        </div>
      )}

      {counts.zonasiz > 0 && (
        <div style={{
          background: '#f0eaff', color: '#5b32c4', padding: '12px 16px',
          borderRadius: '10px', fontSize: '13px', marginBottom: '16px',
          border: '1px solid #d9c9ff'
        }}>
          ⚙️ <strong>{tr("{zonasiz} ta xodimga ish joyi biriktirilmagan.", { zonasiz: counts.zonasiz })}</strong>{tr("Ular belgilanganda \"ish joyida\" deb tan olinmaydi — Foydalanuvchilar sahifasidan har biriga o'z ish joyini biriktiring.")}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: tr("Ish joyida"), value: counts.ish_joyida, color: '#2d8a2d' },
          { label: tr("Tekshiruvda"), value: counts.tekshiruvda, color: '#1a5fb4' },
          { label: tr("Hududdan chiqqan"), value: counts.hududdan_chiqqan, color: '#c2621a' },
          { label: tr("Zona tashqarida"), value: counts.tashqarida, color: '#c47f17' },
          { label: tr("GPS o'chiq"), value: counts.gps_ochiq, color: '#c93636' },
          { label: tr("Belgilanmagan"), value: counts.belgilanmagan, color: '#999' }
        ].map(c => (
          <div key={c.label} style={{ ...card, padding: '18px' }}>
            <div style={{ color: '#888', fontSize: '13px', marginBottom: '6px' }}>{c.label}</div>
            <div style={{ color: c.color, fontSize: '26px', fontWeight: '600' }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div style={card}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => setMode('today')} style={tabStyle(mode === 'today')}>
            {tr("Bugungi holat")}
          </button>
          <button onClick={() => setMode('history')} style={tabStyle(mode === 'history')}>
            {tr("Tarix")}
          </button>

          {mode === 'today' ? (
            <input
              type="text"
              placeholder={tr("🔍 Xodim ismi yoki logini...")}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: '220px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
            />
          ) : (
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
            />
          )}

          <button onClick={load} style={{ ...tabStyle(false), background: '#eef1f7' }}>
            ↻ {tr("Yangilash")}
          </button>
        </div>

        {loading && <p style={{ color: '#999', fontSize: '14px' }}>{tr("Yuklanmoqda...")}</p>}

        {mode === 'today' && !loading && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Xodim")}</th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Holat")}</th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Vaqt")}</th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Tafsilot")}</th>
                <th style={{ textAlign: 'right', padding: '10px 8px', color: '#888', fontWeight: '500' }}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const status = getStatus(r);
                const s = STATUS_STYLE[status];
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '10px 8px' }}>
                      <div style={{ fontWeight: '500' }}>{r.full_name}</div>
                      <div style={{ color: '#999', fontSize: '12px', marginTop: '2px' }}>
                        {[r.badge_number, r.rank, r.department].filter(Boolean).join(' · ')}
                      </div>
                      <div style={{ fontSize: '12px', marginTop: '2px' }}>
                        {r.assigned_zone_name
                          ? <span style={{ color: '#4f8ef7' }}>📍 {r.assigned_zone_name}</span>
                          : <span style={{ color: '#7c4ff7' }}>⚙️ {tr("ish joyi biriktirilmagan")}</span>}
                      </div>
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      <span style={{
                        background: s.bg, color: s.color,
                        padding: '3px 12px', borderRadius: '20px',
                        fontSize: '12px', whiteSpace: 'nowrap'
                      }}>
                        {s.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 8px', color: '#555' }}>
                      {r.created_at ? (
                        <>
                          {formatTime(r.created_at)}
                          <span style={{ color: '#aaa', fontSize: '12px', marginLeft: '6px' }}>
                            ({turNomi(r)})
                          </span>
                        </>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '10px 8px', color: '#888', fontSize: '13px' }}>
                      {status === 'ish_joyida' && r.zone_name}
                      {status === 'tekshiruvda' && (
                        <div style={{ color: '#1a5fb4' }}>
                          {r.note || tr("Maydonda — nazorat tashrifi")}
                        </div>
                      )}
                      {status === 'hududdan_chiqqan' && (
                        <>
                          <div style={{ color: '#c2621a' }}>
                            {tr("Izoh:")} {r.note || <em style={{ color: '#bbb' }}>{tr("yozilmagan")}</em>}
                          </div>
                          {r.distance_meters != null && (
                            <div style={{ color: '#aaa', marginTop: '2px' }}>
                              {tr("Ish joyidan {distance_meters} m uzoqda · hali qaytmagan", { distance_meters: r.distance_meters })}
                            </div>
                          )}
                        </>
                      )}
                      {status === 'tashqarida' && tr("O'z ish joyidan {distance_meters} m uzoqda", { distance_meters: r.distance_meters })}
                      {status === 'zonasiz' && tr("Ish joyi biriktirilmagan — tekshirib bo'lmaydi")}
                      {status === 'gps_ochiq' && (
                        <>
                          {REASON_LABELS[r.reason] || r.reason}
                          {r.note && <div style={{ color: '#aaa', marginTop: '2px' }}>{r.note}</div>}
                        </>
                      )}
                      {status === 'belgilanmagan' && tr("Bugun belgilanmagan")}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.latitude != null && (
                        <button
                          onClick={() => setMapRow(r)}
                          style={{
                            padding: '6px 12px', background: '#eef1f7', color: '#1a1a2e',
                            border: '1px solid #ddd', borderRadius: '8px',
                            fontSize: '12px', cursor: 'pointer'
                          }}
                        >
                          🗺️ {tr("Xaritada")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {mode === 'today' && !loading && visible.length === 0 && (
          <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', margin: '20px 0 0' }}>
            {rows.length === 0 ? tr("Hali xodim yo'q.") : tr("Hech narsa topilmadi.")}
          </p>
        )}

        {mode === 'history' && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Sana va vaqt")}</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Xodim")}</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Turi")}</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Holat")}</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => {
                  const status = getStatus(h);
                  const s = STATUS_STYLE[status];
                  return (
                    <tr key={h.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                      <td style={{ padding: '10px 8px', color: '#555' }}>{formatDateTime(h.created_at)}</td>
                      <td style={{ padding: '10px 8px' }}>
                        {h.full_name}
                        <span style={{ color: '#999', fontSize: '12px', marginLeft: '6px' }}>
                          {h.badge_number}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px', color: '#888' }}>
                        {turNomi(h)}
                        {h.note && (
                          <div style={{ color: '#c2621a', fontSize: '12px', marginTop: '2px' }}>
                            {tr("Izoh: {note}", { note: h.note })}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{
                          background: s.bg, color: s.color,
                          padding: '3px 12px', borderRadius: '20px', fontSize: '12px'
                        }}>
                          {s.label}
                        </span>
                        {h.latitude && (
                          <a
                            href={`https://www.google.com/maps?q=${h.latitude},${h.longitude}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ marginLeft: '8px', color: '#4f8ef7', fontSize: '12px' }}
                          >
                            {tr("xaritada ↗")}
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {history.length === 0 && (
              <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', margin: '20px 0 0' }}>
                {tr("Bu sanada yozuv yo'q.")}
              </p>
            )}
          </>
        )}
      </div>

      {mapRow && (
        <div
          onClick={() => setMapRow(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', zIndex: 1000
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '12px', padding: '20px',
              width: '100%', maxWidth: '760px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '14px' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '18px' }}>{mapRow.full_name}</h3>
                <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>
                  {formatDateTime(mapRow.created_at)} · {mapRow.type}
                  {mapRow.assigned_zone_name && ` · 📍 ${mapRow.assigned_zone_name}`}
                </div>
                <div style={{ marginTop: '6px' }}>
                  <span style={{
                    background: STATUS_STYLE[getStatus(mapRow)].bg,
                    color: STATUS_STYLE[getStatus(mapRow)].color,
                    padding: '3px 12px', borderRadius: '20px', fontSize: '12px'
                  }}>
                    {STATUS_STYLE[getStatus(mapRow)].label}
                  </span>
                  {mapRow.distance_meters != null && (
                    <span style={{ color: '#888', fontSize: '13px', marginLeft: '10px' }}>
                      {tr("Ish joyidan {distance_meters} m", { distance_meters: mapRow.distance_meters })}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setMapRow(null)}
                style={{
                  background: 'transparent', border: 'none', fontSize: '22px',
                  color: '#999', cursor: 'pointer', lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <LocationMap
              latitude={mapRow.latitude}
              longitude={mapRow.longitude}
              accuracy={mapRow.accuracy_meters}
              zoneLatitude={mapRow.zone_latitude}
              zoneLongitude={mapRow.zone_longitude}
              zoneRadius={mapRow.zone_radius}
              zoneName={mapRow.assigned_zone_name}
              personName={mapRow.full_name}
            />

            <div style={{
              display: 'flex', gap: '16px', marginTop: '12px',
              fontSize: '12px', color: '#888', flexWrap: 'wrap', alignItems: 'center'
            }}>
              <span>
                <span style={{
                  display: 'inline-block', width: '10px', height: '10px',
                  background: '#2d8a2d', opacity: 0.4, borderRadius: '2px', marginRight: '5px'
                }} />
                {tr("Ish zonasi")}
              </span>
              <span>📍 {tr("Xodim joylashuvi")}</span>
              {mapRow.accuracy_meters != null && (
                <span>
                  <span style={{
                    display: 'inline-block', width: '10px', height: '10px',
                    background: '#4f8ef7', opacity: 0.3, borderRadius: '2px', marginRight: '5px'
                  }} />
                  {tr("GPS aniqligi ±{round} m", { round: Math.round(mapRow.accuracy_meters) })}
                </span>
              )}
              <a
                href={`https://www.google.com/maps?q=${mapRow.latitude},${mapRow.longitude}`}
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: 'auto', color: '#4f8ef7' }}
              >
                {tr("Google Maps'da ochish ↗")}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Attendance;
